import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building,
  Calculator,
  Globe,
  Users,
  FileCheck,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ServicesPage = () => {
  const { language } = useLanguage();
  const t = translations.services;
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const services = [
    {
      icon: Building,
      title: t.businessReg[language],
      color: isMobile ? 'bg-primary/90' : 'bg-slate-600',
      items: [
        t.companySetup[language],
        t.branchSetup[language],
        t.specialPermit[language],
        t.directorChange[language],
        t.businessChange[language],
        t.otherChange[language]
      ]
    },
    {
      icon: Calculator,
      title: t.accounting[language],
      color: isMobile ? 'bg-secondary/90' : 'bg-amber-600',
      items: [
        t.financialStatement[language],
        t.taxConsulting[language],
        t.bookkeeping[language],
        t.monthlyClose[language],
        t.otherAccounting[language]
      ]
    },
    {
      icon: Globe,
      title: t.offshore[language],
      color: isMobile ? 'bg-primary/85' : 'bg-teal-600',
      items: [
        t.offshoreInvest[language],
        t.workPermit[language],
        t.offshoreBranch[language],
        t.repOffice[language],
        t.otherCompany[language]
      ]
    },
    {
      icon: Users,
      title: t.payroll[language],
      color: isMobile ? 'bg-slate-600' : 'bg-indigo-600',
      items: [
        t.payrollCalc[language],
        t.payrollTransfer[language],
        t.hrInsurance[language],
        t.approvalSystem[language]
      ]
    },
    {
      icon: FileCheck,
      title: t.audit[language],
      color: isMobile ? 'bg-primary/80' : 'bg-rose-600',
      items: [
        t.auditCert[language],
        t.financialCert[language],
        t.taxCert[language],
        t.otherAudit[language]
      ]
    },
    {
      icon: FileText,
      title: t.taxFiling[language],
      color: isMobile ? 'bg-amber-700' : 'bg-orange-600',
      items: [
        t.corpTax[language],
        t.withholdingTax[language],
        t.personalTax[language],
        t.estateTax[language],
        t.otherTax[language]
      ]
    }
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="services-page">
      {/* Hero Section - More compact on mobile */}
      <section className={`bg-gradient-to-b from-primary/5 to-white ${
        isMobile ? 'py-5' : 'py-8 md:py-16 lg:py-24'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className={`font-bold font-noto-serif text-primary ${
              isMobile ? 'text-2xl mb-2' : 'text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-6'
            }`}>
              {t.pageTitle[language]}
            </h1>
            <p className={`text-text-secondary leading-relaxed font-noto-sans max-w-2xl mx-auto ${
              isMobile ? 'text-sm' : 'text-base md:text-lg lg:text-xl'
            }`}>
              {isMobile 
                ? (t.pageSubtitleMobile?.[language] || t.pageSubtitle[language])
                : t.pageSubtitle[language]
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid - Optimized for mobile */}
      <section className={isMobile ? 'py-4' : 'py-6 md:py-16 lg:py-24'}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${
            isMobile ? 'gap-3' : 'gap-4 md:gap-6'
          }`}>
            {services.map((service, index) => {
              const IconComponent = service.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden border border-gray-100/80"
                  data-testid={`service-card-${index}`}
                >
                  {/* Card Header - More compact on mobile */}
                  <div className={`${service.color} px-4 py-3 md:p-5 lg:p-6`}>
                    <div className="flex items-center gap-2.5 md:gap-3">
                      <IconComponent className="text-white flex-shrink-0" size={isMobile ? 22 : 28} />
                      <h3 className="text-base md:text-lg lg:text-xl font-semibold font-noto-serif text-white">{service.title}</h3>
                    </div>
                  </div>

                  {/* Card Content - Tighter on mobile */}
                  <ul className="px-4 py-4 md:px-6 md:py-6 lg:p-8 space-y-2.5 md:space-y-3 lg:space-y-4">
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-baseline gap-2.5 md:gap-3 font-noto-sans text-text-primary">
                        <span className="text-secondary text-sm md:text-base flex-shrink-0">•</span>
                        <span className="text-sm md:text-base leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - Lighter and more compact on mobile */}
      <section className={`relative overflow-hidden ${
        isMobile ? 'py-6 bg-gray-50' : 'py-10 md:py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white'
      }`}>
        {/* Subtle background - hidden on mobile for cleaner look */}
        {!isMobile && (
          <div 
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/30869731/pexels-photo-30869731.jpeg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>
        )}
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl md:max-w-3xl mx-auto"
          >
            <h2 className={`font-semibold font-noto-serif text-primary ${
              isMobile ? 'text-xl mb-2' : 'text-2xl md:text-3xl lg:text-4xl mb-3 md:mb-6'
            }`}>
              {t.ctaTitle[language]}
            </h2>
            <p className={`text-text-secondary font-noto-sans leading-relaxed max-w-xl mx-auto ${
              isMobile ? 'text-sm mb-4' : 'text-sm md:text-base lg:text-lg mb-6 md:mb-8'
            }`}>
              {t.ctaText[language]}
            </p>
            <div className={`flex flex-col sm:flex-row items-center justify-center ${
              isMobile ? 'gap-2' : 'gap-3 md:gap-4'
            }`}>
              <Link
                to="/contact"
                className={`w-full sm:w-auto bg-secondary text-primary rounded-md font-noto-sans font-medium hover:bg-secondary/90 transition-all shadow-sm hover:shadow-md ${
                  isMobile ? 'px-5 py-2 text-sm' : 'px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base'
                }`}
                data-testid="cta-contact"
              >
                {t.ctaContact[language]}
              </Link>
              <Link
                to="/news"
                className={`w-full sm:w-auto border border-primary/60 text-primary rounded-md font-noto-sans font-medium hover:bg-primary hover:text-white transition-all ${
                  isMobile ? 'px-5 py-2 text-sm' : 'px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base'
                }`}
                data-testid="cta-news"
              >
                {t.ctaNews[language]}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
